import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { SessionStorageService } from '../services/session-storage.service';
import { ToolbarService } from '../services/toolbar.service';
import { NotificationDevice, ProductivityIncident, ProductivityNotification, ProductivitySummary, ProductivityTask } from './productivity.model';

@Injectable({ providedIn: 'root' })
export class ProductivityService {
  private http = inject(HttpClient);
  private sessionStorageService = inject(SessionStorageService);
  private toolbarService = inject(ToolbarService);
  private readonly baseUrl = `${environment.restApiUrl}productivity`;
  private refreshSummarySource = new Subject<void>();
  public refreshSummary$ = this.refreshSummarySource.asObservable();

  getCompanyId(): number {
    return this.sessionStorageService.getCompany()?.id || this.toolbarService.customer()?.company?.id || 0;
  }

  getCustomerId(): number {
    return this.sessionStorageService.getCustomer()?.id || this.toolbarService.customer()?.id || 0;
  }

  refreshSummary(): void {
    this.refreshSummarySource.next();
  }

  getSummary(companyId = this.getCompanyId(), customerId = this.getCustomerId()): Observable<APIResponseWrapper<ProductivitySummary>> {
    return this.http.get<APIResponseWrapper<ProductivitySummary>>(`${this.baseUrl}/summary`, {
      params: new HttpParams().set('companyId', companyId).set('customerId', customerId)
    });
  }

  getNotifications(unreadOnly = false): Observable<APIResponseWrapper<ProductivityNotification[]>> {
    return this.http.get<APIResponseWrapper<ProductivityNotification[]>>(`${this.baseUrl}/notifications`, {
      params: new HttpParams()
        .set('companyId', this.getCompanyId())
        .set('customerId', this.getCustomerId())
        .set('unreadOnly', unreadOnly)
    });
  }

  markNotificationRead(id: number): Observable<APIResponseWrapper<ProductivityNotification>> {
    return this.http.post<APIResponseWrapper<ProductivityNotification>>(`${this.baseUrl}/notifications/${id}/read`, {}, {
      params: new HttpParams().set('companyId', this.getCompanyId()).set('customerId', this.getCustomerId())
    });
  }

  markAllNotificationsRead(): Observable<APIResponseWrapper<void>> {
    return this.http.post<APIResponseWrapper<void>>(`${this.baseUrl}/notifications/read-all`, {}, {
      params: new HttpParams().set('companyId', this.getCompanyId()).set('customerId', this.getCustomerId())
    });
  }

  registerDevice(token: string): Observable<APIResponseWrapper<NotificationDevice>> {
    return this.http.post<APIResponseWrapper<NotificationDevice>>(`${this.baseUrl}/notification-devices/register`, {
      companyId: this.getCompanyId(),
      customerId: this.getCustomerId(),
      token,
      platform: 'WEB'
    });
  }

  getTasks(filters: { assignedCustomerId?: number; status?: string; resourceKey?: string; resourceId?: number; incidentId?: number } = {}): Observable<APIResponseWrapper<ProductivityTask[]>> {
    let params = new HttpParams().set('companyId', this.getCompanyId());
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<APIResponseWrapper<ProductivityTask[]>>(`${this.baseUrl}/tasks`, { params });
  }

  saveTask(task: ProductivityTask): Observable<APIResponseWrapper<ProductivityTask>> {
    const customerId = this.getCustomerId();
    return this.http.post<APIResponseWrapper<ProductivityTask>>(`${this.baseUrl}/tasks`, {
      ...task,
      companyId: task.companyId || this.getCompanyId(),
      createdByCustomerId: task.id ? task.createdByCustomerId : customerId,
      updatedByCustomerId: task.id ? customerId : task.updatedByCustomerId
    });
  }

  updateTaskStatus(id: number, status: string, completionNotes?: string): Observable<APIResponseWrapper<ProductivityTask>> {
    return this.http.post<APIResponseWrapper<ProductivityTask>>(`${this.baseUrl}/tasks/${id}/status`, {
      status,
      completionNotes,
      updatedByCustomerId: this.getCustomerId()
    }, { params: new HttpParams().set('companyId', this.getCompanyId()) });
  }

  deleteTask(id: number): Observable<APIResponseWrapper<void>> {
    return this.http.delete<APIResponseWrapper<void>>(`${this.baseUrl}/tasks/${id}`, {
      params: new HttpParams().set('companyId', this.getCompanyId())
    });
  }

  getIncidents(filters: { status?: string; severity?: string; resourceKey?: string; resourceId?: number } = {}): Observable<APIResponseWrapper<ProductivityIncident[]>> {
    let params = new HttpParams().set('companyId', this.getCompanyId());
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return this.http.get<APIResponseWrapper<ProductivityIncident[]>>(`${this.baseUrl}/incidents`, { params });
  }

  getIncident(id: number): Observable<APIResponseWrapper<ProductivityIncident>> {
    return this.http.get<APIResponseWrapper<ProductivityIncident>>(`${this.baseUrl}/incidents/${id}`, {
      params: new HttpParams().set('companyId', this.getCompanyId())
    });
  }

  saveIncident(incident: ProductivityIncident): Observable<APIResponseWrapper<ProductivityIncident>> {
    return this.http.post<APIResponseWrapper<ProductivityIncident>>(`${this.baseUrl}/incidents`, {
      ...incident,
      companyId: incident.companyId || this.getCompanyId(),
      reporterCustomerId: incident.reporterCustomerId || this.getCustomerId()
    });
  }

  updateIncidentStatus(id: number, status: string): Observable<APIResponseWrapper<ProductivityIncident>> {
    return this.http.post<APIResponseWrapper<ProductivityIncident>>(`${this.baseUrl}/incidents/${id}/status`, {
      status,
      updatedByCustomerId: this.getCustomerId()
    }, { params: new HttpParams().set('companyId', this.getCompanyId()) });
  }

  resolveIncident(id: number, resolutionNotes: string): Observable<APIResponseWrapper<ProductivityIncident>> {
    return this.http.post<APIResponseWrapper<ProductivityIncident>>(`${this.baseUrl}/incidents/${id}/resolve`, {
      resolutionNotes,
      resolvedByCustomerId: this.getCustomerId()
    }, { params: new HttpParams().set('companyId', this.getCompanyId()) });
  }
}
